import { useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { agencyService } from '../../../../services/agencyService';
import { normalizeAgencyName } from '../../../../assets/helpers/funciones';
import {
  agencyRoles,
  INDEPENDENT_AGENCY_LABEL,
} from '../../../../assets/helpers/options';
import './AgencySection.scss';

const NEW_AGENCY_VALUE = '__new_agency__';
const independentOption = { value: null, label: INDEPENDENT_AGENCY_LABEL };

export const initialAgencyState = {
  agency: null, // { _id, name }
  newAgencyName: null,
  agencyRole: agencyRoles?.ASESOR,
  draftName: null, // text typed in "+ Nueva agencia" that wasn't added yet
};

const removeAccents = (text) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Searches agencies by name and returns only the active ones
const searchAgencies = async (search) => {
  const response = await agencyService.getAgencies({ search, limit: 50 });
  if (response?.status !== 200) return [];
  return (response?.data?.agencies || []).filter((a) => a?.isActive);
};

// Looks for an agency (active or not) whose normalized name matches. It
// searches by the longest word, with and without accents, so the results
// are as narrow as possible and "Ávila" also finds "Avila"
const findDuplicatedAgency = async (name) => {
  const normalized = normalizeAgencyName(name);
  const longestWord = name
    .split(/[\s,.;:]+/)
    .reduce((a, b) => (b.length > a.length ? b : a), '');
  const searches = [...new Set([longestWord, removeAccents(longestWord)])];

  const responses = await Promise.all(
    searches.map((search) => agencyService.getAgencies({ search, limit: 50 }))
  );
  const failed = responses.find((r) => r?.status !== 200);
  if (failed) {
    return { error: failed?.message || 'No se pudo validar la agencia' };
  }

  const duplicated = responses
    .flatMap((r) => r?.data?.agencies || [])
    .find((a) => normalizeAgencyName(a?.name) === normalized);
  return { duplicated };
};

export const AgencySection = (props) => {
  const { value, onChange } = props;
  const [newNameError, setNewNameError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const debounceRef = useRef(null);

  const isCreating = typeof value?.draftName === 'string';
  const newName = value?.draftName || '';
  const agencyName = value?.newAgencyName || value?.agency?.name;
  const hasAgency = !!(value?.agency || value?.newAgencyName);

  const loadOptions = (search) =>
    new Promise((resolve) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        const agencies = await searchAgencies(search);
        resolve([
          independentOption,
          ...agencies.map((a) => ({ value: a?._id, label: a?.name })),
        ]);
      }, 300);
    });

  const selectedOption = () => {
    if (value?.newAgencyName) {
      return {
        value: NEW_AGENCY_VALUE,
        label: `${value?.newAgencyName} (nueva)`,
      };
    }
    if (value?.agency) {
      return {
        value: value?.agency?._id,
        label: value?.agency?.name || 'Cargando...',
      };
    }
    return independentOption;
  };

  const handleSelect = (option) => {
    if (!option?.value) {
      onChange({ ...initialAgencyState });
      return;
    }
    onChange({
      ...value,
      agency: { _id: option?.value, name: option?.label },
      newAgencyName: null,
    });
  };

  const setNewName = (draftName) => onChange({ ...value, draftName });

  const openCreate = () => {
    setNewNameError('');
    setNewName('');
  };

  const cancelCreate = () => {
    setNewNameError('');
    setNewName(null);
  };

  const addAgency = async () => {
    const name = newName?.trim();
    if (!normalizeAgencyName(name)) {
      setNewNameError('Ingrese el nombre de la agencia');
      return;
    }

    setIsValidating(true);
    const { duplicated, error } = await findDuplicatedAgency(name);
    setIsValidating(false);

    if (error) {
      setNewNameError(error);
      return;
    }
    if (duplicated?.isActive) {
      setNewNameError(
        `Ya existe: ${duplicated?.name}. Selecciónala de la lista.`
      );
      return;
    }
    if (duplicated) {
      setNewNameError(
        `Ya existe: ${duplicated?.name}, pero está inactiva. Contacta a un administrador para reactivarla.`
      );
      return;
    }

    onChange({ ...value, agency: null, newAgencyName: name, draftName: null });
  };

  return (
    <div className="col-12">
      <div className="agency-section">
        <h6 className="agency-section-title">Agencia del intermediario</h6>
        <p className="agency-section-subtitle">
          Si el intermediario pertenece a una empresa de corretaje, vincúlalo a
          su agencia.
        </p>
        <div className="row">
          <div className="col-12 col-md-6">
            <div className="agency-section-label">
              <label>Agencia *</label>
              {!isCreating && (
                <button
                  type="button"
                  className="agency-section-link"
                  onClick={openCreate}
                >
                  + Nueva agencia
                </button>
              )}
            </div>
            {isCreating ? (
              <>
                <div className="agency-section-new">
                  <input
                    type="text"
                    className="form-control"
                    value={newName}
                    placeholder="Nombre de la agencia"
                    onChange={(e) => {
                      setNewName(e.target.value);
                      setNewNameError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAgency();
                      }
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="agency-section-add"
                    onClick={addAgency}
                    disabled={isValidating}
                  >
                    Agregar
                  </button>
                  <button
                    type="button"
                    className="agency-section-cancel"
                    onClick={cancelCreate}
                    aria-label="Cancelar nueva agencia"
                  >
                    ✕
                  </button>
                </div>
                {newNameError && (
                  <p className="agency-section-error">{newNameError}</p>
                )}
              </>
            ) : (
              <>
                <AsyncSelect
                  classNamePrefix="agency-select"
                  cacheOptions
                  defaultOptions
                  loadOptions={loadOptions}
                  value={selectedOption()}
                  onChange={handleSelect}
                  placeholder="Buscar agencia..."
                  noOptionsMessage={() => 'Sin resultados'}
                  loadingMessage={() => 'Buscando...'}
                />
                <p className="agency-section-help">Con buscador por nombre.</p>
              </>
            )}
          </div>
          {hasAgency && !isCreating && (
            <div className="col-12 col-md-6">
              <div className="agency-section-label">
                <label>Rol en la agencia *</label>
              </div>
              <div className="agency-role-toggle">
                {[agencyRoles?.ASESOR, agencyRoles?.MASTER].map((role) => (
                  <button
                    key={role}
                    type="button"
                    className={value?.agencyRole === role ? 'active' : ''}
                    onClick={() => onChange({ ...value, agencyRole: role })}
                  >
                    {role}
                  </button>
                ))}
              </div>
              <p className="agency-section-help">
                {value?.agencyRole === agencyRoles?.MASTER
                  ? `Verá las ventas y comisiones de todos los asesores de ${agencyName}.`
                  : 'Solo verá sus propias ventas.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
